import { CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const SOURCE = 'cart-gift-wrap';

/**
 * Garante endpoint Ajax `/cart/change.js` (evita respostas mais lentas ou inconsistentes).
 *
 * @param {string | undefined} url
 * @returns {string}
 */
function normalizeCartChangeJsUrl(url) {
  if (!url || typeof url !== 'string') return '/cart/change.js';
  const t = url.trim();
  if (t.endsWith('.js')) return t;
  return `${t.replace(/\/$/, '')}.js`;
}

/**
 * Rotas: data-* no HTML (Liquid); fallback `window.Theme` (exposto em scripts.liquid).
 *
 * @param {HTMLElement} root
 */
function getCartRoutes(root) {
  const add = root.dataset.cartAddUrl;
  const cartJs = root.dataset.cartJsUrl;
  const change = root.dataset.cartChangeUrl;
  if (add && cartJs && change) {
    return {
      cartAddUrl: add,
      cartJsUrl: cartJs,
      cartChangeUrl: normalizeCartChangeJsUrl(change),
    };
  }

  const t = typeof globalThis !== 'undefined' && globalThis.Theme && globalThis.Theme.routes;
  if (t) {
    const base = t.cart_url || '/cart';
    const cartJsUrl = base.endsWith('.js') ? base : `${base.replace(/\/$/, '')}.js`;
    return {
      cartAddUrl: t.cart_add_url || '/cart/add.js',
      cartJsUrl,
      cartChangeUrl: normalizeCartChangeJsUrl(t.cart_change_url),
    };
  }

  return {
    cartAddUrl: '/cart/add.js',
    cartJsUrl: '/cart.js',
    cartChangeUrl: '/cart/change.js',
  };
}

/**
 * @returns {string}
 */
function collectCartSectionsParam() {
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });
  return sectionIds.join(',');
}

/**
 * @param {Record<string, string> | undefined} sectionsFromResponse
 */
async function refreshCartFromResponse(sectionsFromResponse) {
  const components = [...document.querySelectorAll('cart-items-component')].filter(
    (el) => el instanceof HTMLElement && el.dataset.sectionId
  );

  await Promise.all(
    components.map(async (el) => {
      const sid = /** @type {string} */ (el.dataset.sectionId);

      let html = sectionsFromResponse?.[sid];
      if (!html) {
        // Só obtém HTML: `renderSection` faria morph aqui e o listener de `cart-items` morfa de novo (duplo trabalho).
        html = await sectionRenderer.getSectionHTML(sid, false);
      }
      if (!html) return;

      document.dispatchEvent(
        new CartUpdateEvent({}, sid, {
          source: SOURCE,
          sections: { [sid]: html },
        })
      );
    })
  );
}

/**
 * @param {string} variantId
 * @param {HTMLElement} root
 */
async function addGiftWrap(variantId, root) {
  const routes = getCartRoutes(root);
  const sections = collectCartSectionsParam();
  /** @type {Record<string, unknown>} */
  const body = {
    items: [{ id: Number(variantId), quantity: 1 }],
    sections_url: window.location.pathname,
  };
  if (sections) body.sections = sections;

  const response = await fetch(routes.cartAddUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  /** @type {Record<string, unknown>} */
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Resposta inválida ao adicionar ao carrinho.');
  }

  if (data.status) {
    throw new Error(
      String(data.message || data.description || 'Não foi possível adicionar a embalagem.')
    );
  }

  await refreshCartFromResponse(
    /** @type {Record<string, string> | undefined} */ (data.sections)
  );
}

/**
 * @param {string} variantId
 * @param {HTMLElement} root
 */
async function removeGiftWrap(variantId, root) {
  const routes = getCartRoutes(root);
  const sections = collectCartSectionsParam();

  /** @type {Record<string, unknown>} */
  const payload = {
    quantity: 0,
    sections_url: window.location.pathname,
    ...(sections ? { sections } : {}),
  };

  const lineKey = root.dataset.lineItemKey;
  if (lineKey) {
    payload.id = lineKey;
  } else {
    const cartRes = await fetch(routes.cartJsUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const cart = await cartRes.json();
    const vid = Number(variantId);
    const idx = cart.items.findIndex(
      /** @param {{ variant_id: number }} item */
      (item) => item.variant_id === vid
    );
    if (idx === -1) return;
    payload.line = idx + 1;
  }

  const body = JSON.stringify(payload);

  const response = await fetch(routes.cartChangeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body,
  });

  const text = await response.text();
  /** @type {Record<string, unknown>} */
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('Resposta inválida ao atualizar o carrinho.');
  }

  if (data.errors) {
    throw new Error(typeof data.errors === 'string' ? data.errors : 'Não foi possível remover a embalagem.');
  }

  await refreshCartFromResponse(
    /** @type {Record<string, string> | undefined} */ (data.sections)
  );
}

/**
 * @param {HTMLInputElement} input
 */
async function handleGiftWrapToggle(input) {
  const root = input.closest('[data-cart-gift-wrap]');
  if (!(root instanceof HTMLElement)) return;

  const variantId = root.dataset.variantId;
  if (!variantId) return;

  input.disabled = true;

  try {
    if (input.checked) {
      await addGiftWrap(variantId, root);
    } else {
      await removeGiftWrap(variantId, root);
    }
  } catch (e) {
    input.checked = !input.checked;
    const message = e instanceof Error ? e.message : 'Erro ao atualizar o carrinho.';
    if (typeof window.alert === 'function') window.alert(message);
  } finally {
    input.disabled = false;
  }
}

function onChange(/** @type {Event} */ event) {
  const t = event.target;
  if (!(t instanceof HTMLInputElement)) return;
  if (!t.hasAttribute('data-cart-gift-wrap-input')) return;
  void handleGiftWrapToggle(t);
}

if (typeof document !== 'undefined') {
  document.addEventListener('change', onChange);
}
